// Layout constants for consistent positioning across the app

export const CONTEXT_PANEL_WIDTH = 300;

// Vertical padding for page-level Containers
export const PAGE_PY = {
  content: 0, // Standard content pages (home, offers, guides, admin, etc.)
  auxiliary: 4, // Standalone pages (register, oauth, invite, account, help, etc.)
  detail: 2, // Detail/thread view pages
} as const;

// New unified layout configuration
export const LAYOUT_CONFIG = {
  appBar: {
    height: { desktop: 64, mobile: 56 },
    elevation: 1,
    zIndex: 1200,
  },
  contextPanel: {
    height: 60,
    width: 300,
    elevation: 1,
    zIndex: 1100,
  },
  // Visual separator when bars are stacked
  separator: {
    color: 'divider',
    width: 1,
  },
};

// Simple, clear positioning functions
export const getAppBarStyle = (position: 'top' | 'bottom', isNarrow: boolean) => {
  const height = isNarrow
    ? LAYOUT_CONFIG.appBar.height.mobile
    : LAYOUT_CONFIG.appBar.height.desktop;

  return {
    height,
    minHeight: height, // Ensure minimum height is maintained
    top: position === 'top' ? 0 : 'auto',
    bottom: position === 'bottom' ? 0 : 'auto',
    position: 'fixed' as const,
    left: 0,
    right: 0,
    zIndex: LAYOUT_CONFIG.appBar.zIndex,
  };
};

export const getContextPanelStyle = (
  appBarPosition: 'top' | 'bottom',
  variant: 'toolbar' | 'sidebar',
  isNarrow: boolean
) => {
  // Sidebar layout for wide desktop
  if (variant === 'sidebar') {
    return {
      position: 'fixed' as const,
      top: LAYOUT_CONFIG.appBar.height.desktop,
      bottom: 0,
      right: 0,
      width: LAYOUT_CONFIG.contextPanel.width,
      zIndex: LAYOUT_CONFIG.contextPanel.zIndex,
    };
  }

  const appBarHeight = isNarrow
    ? LAYOUT_CONFIG.appBar.height.mobile
    : LAYOUT_CONFIG.appBar.height.desktop;

  // Mobile: context panel goes on BOTTOM, ABOVE the app bar
  // Narrow desktop: context panel goes on TOP, BELOW the app bar
  // Always fixed positioning to ensure no gaps
  return {
    position: 'fixed' as const,
    height: LAYOUT_CONFIG.contextPanel.height,
    left: 0,
    right: 0,
    zIndex: LAYOUT_CONFIG.contextPanel.zIndex,
    // Critical logic: position based on appBarPosition
    top: appBarPosition === 'top' ? appBarHeight : 'auto',
    // For bottom position, account for safe area
    bottom:
      appBarPosition === 'bottom'
        ? `calc(${appBarHeight}px + env(safe-area-inset-bottom))`
        : 'auto',
  };
};

// Scroll margin to position permalink targets below the fixed AppBar.
// Parameterized on viewport width so narrow/mobile screens use the
// mobile app-bar height, matching every other offset helper in this file.
export const getScrollMarginTop = (isNarrow: boolean): number =>
  (isNarrow ? LAYOUT_CONFIG.appBar.height.mobile : LAYOUT_CONFIG.appBar.height.desktop) + 16;
